import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  LinearProgress,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import FlagIcon from "@mui/icons-material/Flag";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import PreviewOutlinedIcon from "@mui/icons-material/PreviewOutlined";
import { bonusApi } from "../../Components/bonusApi";
import { getProgramsList, getCoursesList } from "../marketing/marketingApi";
import { toast } from "react-toastify";
import styles from "./CreateBonusWizardModal.module.scss";

const METRICS = [
  { value: "conversion", label: "Conversions" },
  { value: "revenue", label: "Revenue" },
  { value: "conversion_rate", label: "Conversion Rate" },
];

const SCOPES = [
  { value: "overall", label: "Overall" },
  { value: "program", label: "Program" },
  { value: "course", label: "Course" },
];

const REWARD_TYPES = [
  { value: "cash", label: "Cash" },
  { value: "gift", label: "Gift" },
  { value: "event", label: "Event" },
  { value: "custom", label: "Custom" },
];

const TOTAL_STEPS = 4;
const STEP_META = [
  { id: 1, short: "Basics", title: "Name & Scope", icon: <EmojiEventsIcon sx={{ fontSize: 20 }} /> },
  { id: 2, short: "Goal", title: "Goal & Dates", icon: <FlagIcon sx={{ fontSize: 20 }} /> },
  { id: 3, short: "Reward", title: "Reward", icon: <CardGiftcardIcon sx={{ fontSize: 20 }} /> },
  { id: 4, short: "Review", title: "Review & Submit", icon: <PreviewOutlinedIcon sx={{ fontSize: 20 }} /> },
];

const STEP_FIELDS = {
  1: ["name", "description", "metric", "scope_type", "scope_id"],
  2: ["goal", "start_date", "end_date"],
  3: ["reward_type", "reward_description"],
};

const getDefaultForm = () => ({
  name: "",
  description: "",
  metric: "conversion",
  scope_type: "overall",
  scope_id: "",
  goal: "",
  start_date: "",
  end_date: "",
  reward_type: "cash",
  reward_description: "",
});

export default function CreateBonusWizardModal({ open, onClose, onSuccess }) {
  const [programs, setPrograms] = useState([]);
  const [courses, setCourses] = useState([]);
  const [formData, setFormData] = useState(getDefaultForm);
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [createdName, setCreatedName] = useState("");

  const progressPercent = (currentStep / TOTAL_STEPS) * 100;

  useEffect(() => {
    if (open) {
      setFormData(getDefaultForm());
      setErrors({});
      setApiError("");
      setCurrentStep(1);
      setSuccess(false);
      setCreatedName("");
      getProgramsList().then((d) => setPrograms(Array.isArray(d) ? d : [])).catch(() => setPrograms([]));
    }
  }, [open]);

  useEffect(() => {
    if (formData.scope_type === "program" && formData.scope_id) {
      getCoursesList(Number(formData.scope_id)).then((d) => setCourses(Array.isArray(d) ? d : [])).catch(() => setCourses([]));
    } else if (formData.scope_type === "course") {
      getCoursesList(null).then((d) => setCourses(Array.isArray(d) ? d : [])).catch(() => setCourses([]));
    } else {
      setCourses([]);
    }
  }, [formData.scope_type, formData.scope_id]);

  const setFieldValue = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "scope_type" ? { scope_id: "" } : {}),
    }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
    setApiError("");
  };

  const validateStep1 = (data = formData) => {
    const next = {};
    if (!(data.name || "").trim()) next.name = "Bonus name is required.";
    if (data.scope_type === "program" && !data.scope_id) next.scope_id = "Please select a program.";
    if (data.scope_type === "course" && !data.scope_id) next.scope_id = "Please select a course.";
    return next;
  };

  const validateStep2 = (data = formData) => {
    const next = {};
    const goalNum = Number(data.goal);
    if (!data.goal || isNaN(goalNum) || goalNum <= 0) next.goal = "Goal must be a positive number.";
    if (!(data.start_date || "").trim()) next.start_date = "Start date is required.";
    if (!(data.end_date || "").trim()) next.end_date = "End date is required.";
    if (data.start_date && data.end_date && data.end_date < data.start_date) next.end_date = "End date must be on or after start date.";
    return next;
  };

  const applyStepErrors = (step, stepErrors) => {
    const fields = STEP_FIELDS[step] || [];
    setErrors((prev) => {
      const next = { ...prev };
      fields.forEach((f) => delete next[f]);
      return { ...next, ...stepErrors };
    });
  };

  const goToStep = (s) => setCurrentStep(s);

  const handleNext = () => {
    if (currentStep === 1) {
      const e = validateStep1();
      applyStepErrors(1, e);
      if (Object.keys(e).length > 0) return;
    }
    if (currentStep === 2) {
      const e = validateStep2();
      applyStepErrors(2, e);
      if (Object.keys(e).length > 0) return;
    }
    if (currentStep < TOTAL_STEPS) setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const e1 = validateStep1();
    const e2 = validateStep2();
    applyStepErrors(1, e1);
    applyStepErrors(2, e2);
    if (Object.keys(e1).length > 0) {
      setCurrentStep(1);
      return;
    }
    if (Object.keys(e2).length > 0) {
      setCurrentStep(2);
      return;
    }
    setApiError("");
    setIsSubmitting(true);
    try {
      await bonusApi.create({
        name: formData.name.trim(),
        description: (formData.description || "").trim() || null,
        metric: formData.metric,
        scope_type: formData.scope_type,
        scope_id: formData.scope_type === "overall" ? null : (formData.scope_id ? Number(formData.scope_id) : null),
        goal: Number(formData.goal),
        start_date: formData.start_date,
        end_date: formData.end_date,
        reward_type: formData.reward_type,
        reward_description: (formData.reward_description || "").trim() || null,
      });
      setCreatedName(formData.name);
      setSuccess(true);
      toast.success("Bonus created. All employees have been notified.");
      onSuccess?.();
    } catch (err) {
      setApiError(err?.message || "Failed to create bonus.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSuccess(false);
      onClose();
    }
  };

  const resetAndClose = () => {
    setFormData(getDefaultForm());
    setErrors({});
    setCurrentStep(1);
    setSuccess(false);
    setCreatedName("");
    onClose();
  };

  const getScopeDisplay = () => {
    if (formData.scope_type === "overall") return "Overall";
    if (formData.scope_type === "program" && formData.scope_id) {
      const p = programs.find((x) => String(x.id) === String(formData.scope_id));
      return p ? p.name : `Program #${formData.scope_id}`;
    }
    if (formData.scope_type === "course" && formData.scope_id) {
      const c = courses.find((x) => String(x.id) === String(formData.scope_id));
      return c ? c.name : `Course #${formData.scope_id}`;
    }
    return "—";
  };

  const getMetricLabel = (v) => METRICS.find((m) => m.value === v)?.label ?? v;
  const getRewardLabel = (v) => REWARD_TYPES.find((r) => r.value === v)?.label ?? v;
  const renderErr = (f) => (errors[f] ? <div className={styles.errorText} role="alert">{errors[f]}</div> : null);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ className: styles.modalPaper }}
      aria-labelledby="create-bonus-wizard-title"
    >
      <DialogTitle id="create-bonus-wizard-title" className={styles.modalHeader}>
        <Typography variant="h6" fontWeight={700}>
          Create Bonus
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Set up a new office bonus. All employees will be notified by email.
        </Typography>
        {!success && (
          <>
            <div className={styles.progressWrap}>
              <div className={styles.progressText}>Step {currentStep} of {TOTAL_STEPS}</div>
              <LinearProgress variant="determinate" value={progressPercent} sx={{ height: 6, borderRadius: 3 }} aria-label={`Step ${currentStep} of ${TOTAL_STEPS}`} />
            </div>
            <nav className={styles.stepRow} aria-label="Wizard steps">
              {STEP_META.map((step, idx) => {
                const stepNum = idx + 1;
                const status = stepNum < currentStep ? "done" : stepNum === currentStep ? "current" : "locked";
                return (
                  <button
                    key={step.id}
                    type="button"
                    className={`${styles.stepBtn} ${status === "done" ? styles.stepBtnDone : status === "current" ? styles.stepBtnCurrent : styles.stepBtnLocked}`}
                    onClick={() => goToStep(stepNum)}
                    disabled={stepNum > currentStep}
                    aria-label={`Step ${stepNum}: ${step.title}`}
                  >
                    <div className={`${styles.stepBadge} ${status === "done" ? styles.stepBadgeDone : status === "current" ? styles.stepBadgeCurrent : styles.stepBadgeLocked}`}>
                      {status === "done" ? <CheckCircleRoundedIcon sx={{ fontSize: 18 }} /> : step.icon}
                    </div>
                    <Box>
                      <div className={styles.stepShort}>{step.short}</div>
                      <div className={styles.stepTitle}>{step.title}</div>
                    </Box>
                  </button>
                );
              })}
            </nav>
          </>
        )}
      </DialogTitle>

      <DialogContent className={styles.stepBody} dividers={false}>
        {success ? (
          <div className={styles.successBody}>
            <div className={styles.successIcon} aria-hidden />
            <p className={styles.successText}>Bonus created successfully</p>
            <p className={styles.successName}>Name: <strong>{createdName}</strong></p>
            <p className={styles.successHint}>All employees have been notified by email.</p>
          </div>
        ) : (
          <>
            {apiError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApiError("")}>{apiError}</Alert>}
            <form onSubmit={currentStep === TOTAL_STEPS ? handleSubmit : (e) => { e.preventDefault(); handleNext(); }} noValidate>
              {currentStep === 1 && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField fullWidth required label="Bonus Name *" value={formData.name} onChange={(e) => setFieldValue("name", e.target.value)} error={Boolean(errors.name)} helperText={errors.name} size="small" placeholder="e.g. January Conversion Challenge" />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Description" value={formData.description} onChange={(e) => setFieldValue("description", e.target.value)} multiline rows={2} size="small" placeholder="Optional description" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Metric</InputLabel>
                      <Select value={formData.metric} label="Metric" onChange={(e) => setFieldValue("metric", e.target.value)}>
                        {METRICS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Scope</InputLabel>
                      <Select value={formData.scope_type} label="Scope" onChange={(e) => setFieldValue("scope_type", e.target.value)}>
                        {SCOPES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  {formData.scope_type === "program" && (
                    <Grid item xs={12}>
                      <FormControl fullWidth size="small" required error={Boolean(errors.scope_id)}>
                        <InputLabel>Program</InputLabel>
                        <Select value={formData.scope_id} label="Program" onChange={(e) => setFieldValue("scope_id", e.target.value)}>
                          <MenuItem value="">Select program…</MenuItem>
                          {programs.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                        </Select>
                        {renderErr("scope_id")}
                      </FormControl>
                    </Grid>
                  )}
                  {formData.scope_type === "course" && (
                    <Grid item xs={12}>
                      <FormControl fullWidth size="small" required error={Boolean(errors.scope_id)}>
                        <InputLabel>Course</InputLabel>
                        <Select value={formData.scope_id} label="Course" onChange={(e) => setFieldValue("scope_id", e.target.value)}>
                          <MenuItem value="">Select course…</MenuItem>
                          {courses.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                        </Select>
                        {renderErr("scope_id")}
                      </FormControl>
                    </Grid>
                  )}
                </Grid>
              )}

              {currentStep === 2 && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField fullWidth required type="number" inputProps={{ min: 0.01, step: formData.metric === "conversion_rate" ? 0.1 : 1 }} label={formData.metric === "conversion" ? "Conversions needed to win *" : "Goal Value *"} value={formData.goal} onChange={(e) => setFieldValue("goal", e.target.value)} error={Boolean(errors.goal)} helperText={errors.goal || (formData.metric === "conversion" ? "Auto-checked when conversions are recorded." : null)} size="small" placeholder={formData.metric === "conversion_rate" ? "e.g. 25" : "e.g. 10"} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth required type="date" label="Start Date *" value={formData.start_date} onChange={(e) => setFieldValue("start_date", e.target.value)} InputLabelProps={{ shrink: true }} error={Boolean(errors.start_date)} helperText={errors.start_date} size="small" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth required type="date" label="End Date *" value={formData.end_date} onChange={(e) => setFieldValue("end_date", e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ min: formData.start_date || undefined }} error={Boolean(errors.end_date)} helperText={errors.end_date} size="small" />
                  </Grid>
                </Grid>
              )}

              {currentStep === 3 && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Reward Type</InputLabel>
                      <Select value={formData.reward_type} label="Reward Type" onChange={(e) => setFieldValue("reward_type", e.target.value)}>
                        {REWARD_TYPES.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Reward Description" value={formData.reward_description} onChange={(e) => setFieldValue("reward_description", e.target.value)} size="small" placeholder="e.g. Team lunch, $500 bonus" />
                  </Grid>
                </Grid>
              )}

              {currentStep === 4 && (
                <div className={styles.reviewWrap}>
                  <div className={styles.reviewCard}>
                    <div className={styles.reviewHeader}>
                      <h5 className={styles.reviewHeaderTitle}>Basics</h5>
                      <Button type="button" size="small" variant="outlined" onClick={() => goToStep(1)}>Edit</Button>
                    </div>
                    <div className={styles.reviewGrid}>
                      <div className={styles.reviewItem}><span className={styles.reviewLabel}>Name</span><span className={styles.reviewValue}>{formData.name || "—"}</span></div>
                      <div className={styles.reviewItem}><span className={styles.reviewLabel}>Description</span><span className={styles.reviewValue}>{formData.description || "—"}</span></div>
                      <div className={styles.reviewItem}><span className={styles.reviewLabel}>Metric</span><span className={styles.reviewValue}>{getMetricLabel(formData.metric)}</span></div>
                      <div className={styles.reviewItem}><span className={styles.reviewLabel}>Scope</span><span className={styles.reviewValue}>{getScopeDisplay()}</span></div>
                    </div>
                  </div>
                  <div className={styles.reviewCard}>
                    <div className={styles.reviewHeader}>
                      <h5 className={styles.reviewHeaderTitle}>Goal & Dates</h5>
                      <Button type="button" size="small" variant="outlined" onClick={() => goToStep(2)}>Edit</Button>
                    </div>
                    <div className={styles.reviewGrid}>
                      <div className={styles.reviewItem}><span className={styles.reviewLabel}>Goal</span><span className={styles.reviewValue}>{formData.goal || "—"}{formData.metric === "conversion_rate" ? "%" : ""}</span></div>
                      <div className={styles.reviewItem}><span className={styles.reviewLabel}>Start</span><span className={styles.reviewValue}>{formData.start_date || "—"}</span></div>
                      <div className={styles.reviewItem}><span className={styles.reviewLabel}>End</span><span className={styles.reviewValue}>{formData.end_date || "—"}</span></div>
                    </div>
                  </div>
                  <div className={styles.reviewCard}>
                    <div className={styles.reviewHeader}>
                      <h5 className={styles.reviewHeaderTitle}>Reward</h5>
                      <Button type="button" size="small" variant="outlined" onClick={() => goToStep(3)}>Edit</Button>
                    </div>
                    <div className={styles.reviewGrid}>
                      <div className={styles.reviewItem}><span className={styles.reviewLabel}>Type</span><span className={styles.reviewValue}>{getRewardLabel(formData.reward_type)}</span></div>
                      <div className={styles.reviewItem}><span className={styles.reviewLabel}>Description</span><span className={styles.reviewValue}>{formData.reward_description || "—"}</span></div>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </>
        )}
      </DialogContent>

      {!success && (
        <Box className={styles.actions}>
          <Button onClick={currentStep === 1 ? handleClose : handleBack} disabled={isSubmitting} color="inherit">
            {currentStep === 1 ? "Cancel" : "Back"}
          </Button>
          {currentStep < TOTAL_STEPS ? (
            <Button variant="contained" onClick={handleNext} disabled={isSubmitting}>Next</Button>
          ) : (
            <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting} startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}>
              {isSubmitting ? "Creating…" : "Create Bonus"}
            </Button>
          )}
        </Box>
      )}

      {success && (
        <Box className={styles.actions}>
          <Button onClick={resetAndClose}>Back to list</Button>
          <Button variant="contained" onClick={() => { setSuccess(false); setFormData(getDefaultForm()); setCurrentStep(1); setCreatedName(""); }}>Create another</Button>
        </Box>
      )}
    </Dialog>
  );
}
